/**
 * task-api.ts — klient API kartoteki „Zadania" i słownika typów zadań (faza 30, ADR-0123).
 *
 * Jedno miejsce z: kształtami odpowiedzi serwera, kluczami zapytań, hookami odczytu, parametrami
 * listy i mapowaniem kodów błędów serwera na komunikaty. Kontrakt = trasy `apps/api/app/api/tasks/**`
 * i `apps/api/app/api/task-types/**` (oraz ich testy integracyjne).
 *
 * Daty zadań: serwer przyjmuje WYŁĄCZNIE ISO ze strefą i zwraca ISO. Przeliczenie na wartość pola
 * `datetime-local` (czas lokalny przeglądarki) i z powrotem robi strona — `isoToLocalDateTimeInput` /
 * `localDateTimeInputToIso` z `@/lib/local-date`.
 */

import type { TaskPriority, TaskStatus } from '@saas/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type { TFunction } from 'i18next';

import { listLoadRetry } from '@/components/configurable-table/load-error';
import { api } from '@/lib/api';
import type { ProcessColumnsRow } from '@/lib/documents/process-columns';
import type { TaskNumberingReset } from '@/pages/tasks/task-model';

// ============================================================================
// Kształty odpowiedzi
// ============================================================================

/** Typ zadania (wiersz słownika `task_types`). Obieg i układ per typ nie są jeszcze zapisywalne. */
export interface TaskTypeDto {
  id: string;
  companyId: string;
  name: string;
  code: string;
  description: string | null;
  numberingFormat: string | null;
  numberingReset: TaskNumberingReset;
  /**
   * Pole daty, od której liczy się rok i miesiąc w numerze zadania: slug pola rdzennego
   * (`TASK_CORE_DATE_FIELDS`) albo pola dodatkowego kartoteki. `null` = domyślnie data utworzenia.
   */
  numberingBaseField: string | null;
  /** Układ formularza karty zadania tego typu (soft-FK na układ kartoteki Zadania); `null` = kaskada. */
  formLayoutId: string | null;
  /** Domyślny obieg akceptacji zadań tego typu (opublikowany proces o celu `crm_core.tasks`); `null` = brak. */
  defaultProcessId: string | null;
  sortOrder: number;
  isActive: boolean;
  gcrecord: string | null;
}

/**
 * Wiersz listy zadań (silnik list). W trybie projekcji `?columns=` serwer zwraca wiersz CZĘŚCIOWY
 * (typowany tu jak pełny — kompilator tego nie wyłapie): pole czytane poza komórką widocznej kolumny
 * musi trafić do „zawsze dołączanych" slugów strony.
 */
export interface TaskListItem extends ProcessColumnsRow {
  id: string;
  companyId: string;
  taskTypeId: string;
  taskTypeName: string;
  systemNumber: string | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  ownerUserId: string | null;
  contractorId: string | null;
  contractorName: string | null;
  categoryId: string | null;
  categoryName: string | null;
  dueAt: string | null;
  plannedStartAt: string | null;
  plannedEndAt: string | null;
  actualStartAt: string | null;
  actualEndAt: string | null;
  createdByUserId: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  gcrecord: string | null;
  /** Wartości pól dodatkowych widocznych kolumn (slug → wartość). */
  ext?: Record<string, unknown>;
  // Kolumny przekrojowe procesu (`activeProcessName`, `currentStage`) — z `ProcessColumnsRow`: silnik
  // list dokłada je, gdy tenant ma aktywny moduł procesów.
}

/** Odpowiedź `GET /api/tasks` — etykiety odwołań (`extRefLabels`) dokłada silnik list. */
export interface TasksListResponse {
  tasks: TaskListItem[];
  total: number;
  limit: number;
  offset: number;
  meta?: { sortable?: string[]; filterable?: string[] };
  extRefLabels?: Record<string, Record<string, string | null>>;
}

/** Pełny widok zadania (`GET /api/tasks/:id` → `task`). */
export interface TaskDetailDto {
  id: string;
  companyId: string;
  taskTypeId: string;
  taskTypeName: string;
  taskTypeCode: string;
  systemNumber: string | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  ownerUserId: string | null;
  ownerName: string | null;
  contractorId: string | null;
  contractorName: string | null;
  categoryId: string | null;
  categoryName: string | null;
  dueAt: string | null;
  plannedStartAt: string | null;
  plannedEndAt: string | null;
  actualStartAt: string | null;
  actualEndAt: string | null;
  createdByUserId: string | null;
  createdByName: string | null;
  updatedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  gcrecord: string | null;
  /** Wartości pól dodatkowych (slug → wartość). */
  extValues: Record<string, unknown>;
}

/** Prawa akcji na rekordzie liczone przez serwer (podpowiedź dla przycisków karty). */
export interface TaskPermissionsDto {
  canEdit: boolean;
  canDelete: boolean;
}

export interface TaskDetailResponse {
  task: TaskDetailDto;
  permissions: TaskPermissionsDto;
}

// ============================================================================
// Stałe: kody uprawnień, klucze zapytań
// ============================================================================

/** Kody uprawnień kartoteki — wyłącznie podpowiedź w panelu, rozstrzyga serwer. */
export const TASK_PERMISSION = {
  view: 'crm_core.tasks.view',
  edit: 'crm_core.tasks.edit',
  delete: 'crm_core.tasks.delete',
} as const;

export const TASK_TYPES_PERMISSION = {
  view: 'crm_core.task_types.view',
  manage: 'crm_core.task_types.manage',
} as const;

/** Limity długości pól typu zadania — zgodne ze schematem zod tras `/api/task-types`. */
export const TASK_TYPE_LIMITS = {
  code: 30,
  name: 100,
  description: 500,
  numberingFormat: 50,
} as const;

export const TASKS_QUERY_ROOT = 'tasks' as const;
export const TASK_TYPES_QUERY_ROOT = 'task-types' as const;

export const TASK_DETAIL_KEY = (taskId: string) => [TASKS_QUERY_ROOT, taskId, 'detail'] as const;

// ============================================================================
// Parametry listy
// ============================================================================

export interface TasksListFilters {
  companyId?: string;
  /** Wyszukiwanie po tytule i numerze (serwer: `?q=` silnika list). */
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  /** Filtry nagłówków kolumn jako serializowany JSON. */
  headerFilters?: string;
  /** CSV slugów widocznych kolumn (projekcja). */
  columns?: string;
  limit?: number;
  offset?: number;
}

export function buildTasksListParams(filters: TasksListFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.companyId) params.companyId = filters.companyId;
  // Silnik list czyta frazę wyszukiwania z `?q=` (tytuł i numer zadania).
  if (filters.search) params.q = filters.search;
  if (filters.sort) params.sort = filters.sort;
  if (filters.order) params.order = filters.order;
  if (filters.headerFilters) params.headerFilters = filters.headerFilters;
  if (filters.columns) params.columns = filters.columns;
  if (filters.limit !== undefined) params.limit = String(filters.limit);
  if (filters.offset !== undefined) params.offset = String(filters.offset);
  return params;
}

export const TASKS_LIST_KEY = (filters: TasksListFilters) =>
  [TASKS_QUERY_ROOT, 'list', filters] as const;

export function useTasksList(filters: TasksListFilters) {
  return useQuery<TasksListResponse>({
    queryKey: TASKS_LIST_KEY(filters),
    // Poprzednie dane zostają na ekranie podczas odświeżenia (zmiana strony/sortu/filtra, zapis
    // przeciągnięciem na tablicy Kanban) — bez tego lista miga loaderem przy każdym odświeżeniu.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await api.get<TasksListResponse>('/tasks', {
        params: buildTasksListParams(filters),
      });
      return res.data;
    },
    staleTime: 30_000,
    // Jedno automatyczne ponowienie tylko przy awarii; 403/4xx nie ponawiamy (odpowiedź się nie zmieni).
    retry: listLoadRetry,
  });
}

export function useTaskDetail(taskId: string | null) {
  return useQuery<TaskDetailResponse>({
    queryKey: taskId ? TASK_DETAIL_KEY(taskId) : [TASKS_QUERY_ROOT, 'none', 'detail'],
    queryFn: async () => {
      const res = await api.get<TaskDetailResponse>(`/tasks/${taskId}`);
      return res.data;
    },
    enabled: taskId !== null,
    staleTime: 0,
  });
}

// ============================================================================
// Słownik typów
// ============================================================================

export interface TaskTypesQuery {
  /** Zawężenie do jednej firmy (UUID). Brak = wszystkie firmy dostępne użytkownikowi. */
  companyId?: string;
  /** Tylko aktywne (podpowiedź typu przy zakładaniu zadania). */
  activeOnly?: boolean;
  /** Dokłada usunięte typy (widok kosza słownika) — serwer wymaga prawa zarządzania typami. */
  includeDeleted?: boolean;
}

export const TASK_TYPES_LIST_KEY = (query: TaskTypesQuery) =>
  [TASK_TYPES_QUERY_ROOT, 'list', query] as const;

export function useTaskTypes(query: TaskTypesQuery = {}, options: { enabled?: boolean } = {}) {
  return useQuery<TaskTypeDto[]>({
    enabled: options.enabled ?? true,
    queryKey: TASK_TYPES_LIST_KEY(query),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (query.companyId) params.companyId = query.companyId;
      if (query.activeOnly) params.active = '1';
      if (query.includeDeleted) params.include_deleted = 'true';
      const res = await api.get<{ taskTypes: TaskTypeDto[] }>('/task-types', { params });
      return res.data.taskTypes;
    },
    staleTime: 30_000,
  });
}

// ============================================================================
// Błędy serwera → komunikaty
// ============================================================================

/** Rozpoznany kształt błędu serwera zadań / typów zadań. */
export interface TaskApiError {
  status: number | undefined;
  code: string | undefined;
  /**
   * `DATE_ORDER_INVALID`: pole końca pary dat, które jest wcześniejsze niż start.
   * `NUMBERING_BASE_FIELD_REQUIRED` / `NUMBERING_BASE_FIELD_INVALID`: slug pustego (nieczytelnego)
   * pola daty numeracji (`due_at`, slug pola dodatkowego…).
   */
  field: string | undefined;
}

export function parseTaskApiError(err: unknown): TaskApiError {
  if (!isAxiosError(err)) return { status: undefined, code: undefined, field: undefined };
  const data: unknown = err.response?.data;
  const record = typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : {};
  return {
    status: err.response?.status,
    code: typeof record.error === 'string' ? record.error : undefined,
    field: typeof record.field === 'string' ? record.field : undefined,
  };
}

/** Kody serwera, dla których istnieje komunikat `tenant.tasks.errors.<KOD>`. */
export const TASK_ERROR_CODES = [
  'COMPANY_ID_FROM_TASK_TYPE',
  'COMPANY_ID_IMMUTABLE',
  'INVALID_TASK_TYPE',
  'TASK_TYPE_NUMBERING_MISSING',
  'TASK_TYPE_COMPANY_MISMATCH',
  'TASK_TYPE_DELETED',
  'TASK_TYPE_INACTIVE',
  'OWNER_NOT_IN_COMPANY',
  'CONTRACTOR_NOT_IN_COMPANY',
  'CATEGORY_INVALID',
  'TASK_NUMBER_TAKEN',
  'TASK_DELETED',
  'TASK_CONCURRENT_EDIT',
  'DATE_ORDER_INVALID',
  'INVALID_STATUS',
  'INVALID_PRIORITY',
  'TASK_NOT_FOUND',
  'ALREADY_DELETED',
  // Przywracanie (zadania i typu): rekord nie jest usunięty (np. przywrócony równolegle)
  'NOT_DELETED',
  'PERMISSION_DENIED',
  // Słownik typów
  'NUMBERING_FORMAT_RESET_MISMATCH',
  'NUMBERING_FORMAT_REQUIRED',
  'NUMBERING_FORMAT_CONFLICT',
  'NUMBERING_FORMAT_NO_COUNTER',
  'INVALID_NUMBERING_FORMAT',
  'INVALID_NUMBERING_BASE_FIELD',
  // Zapis zadania: puste albo źle skonfigurowane pole daty numeracji typu
  'NUMBERING_BASE_FIELD_REQUIRED',
  'NUMBERING_BASE_FIELD_MISCONFIGURED',
  'NUMBERING_BASE_FIELD_INVALID',
  'TYPE_NUMBERING_LOCKED',
  'TYPE_IN_USE',
  'CODE_TAKEN',
  'NAME_TAKEN',
  'SORT_ORDER_EXHAUSTED',
  'FIELD_NOT_WRITABLE',
  'UNKNOWN_FIELD',
  'NO_COMPANY_ACCESS',
  'COMPANY_NOT_FOUND',
  'COMPANY_INACTIVE',
  'NOT_FOUND',
  // Walidacja ciała żądania (schemat serwera)
  'VALIDATION_ERROR',
  'INVALID_BODY',
] as const;

const KNOWN_TASK_ERROR_CODES: ReadonlySet<string> = new Set(TASK_ERROR_CODES);

/** Kody błędów pola daty numeracji, których komunikat nazywa pole (`{{field}}`). */
const NUMBERING_FIELD_ERROR_CODES: ReadonlySet<string> = new Set([
  'NUMBERING_BASE_FIELD_REQUIRED',
  'NUMBERING_BASE_FIELD_INVALID',
]);

/** Sufiks klucza komunikatu pola daty numeracji, gdy serwer nie podał pola. */
export const NUMBERING_FIELD_UNNAMED_SUFFIX = '_noField';

/**
 * Czytelny komunikat dla błędu zapisu / odczytu. Kod znany → `tenant.tasks.errors.<KOD>`;
 * `DATE_ORDER_INVALID` z polem → komunikat o konkretnej parze dat; błędy pola daty numeracji
 * nazywają pole (`numberingFieldLabel` zamienia slug na nazwę, bez niego — sam slug; bez pola w
 * odpowiedzi — komunikat `<KOD>_noField`). Kod nieznany albo brak odpowiedzi serwera → JAWNY
 * komunikat ogólny (`fallbackKey`), nie cisza.
 */
export function taskErrorMessage(
  err: unknown,
  t: TFunction,
  fallbackKey: string,
  numberingFieldLabel?: (slug: string) => string,
): string {
  const { code, field } = parseTaskApiError(err);
  if (code !== undefined && NUMBERING_FIELD_ERROR_CODES.has(code)) {
    if (field === undefined)
      return t(`tenant.tasks.errors.${code}${NUMBERING_FIELD_UNNAMED_SUFFIX}`);
    return t(`tenant.tasks.errors.${code}`, { field: numberingFieldLabel?.(field) ?? field });
  }
  if (code === 'DATE_ORDER_INVALID' && field === 'plannedEndAt') {
    return t('tenant.tasks.errors.DATE_ORDER_INVALID_planned');
  }
  if (code === 'DATE_ORDER_INVALID' && field === 'actualEndAt') {
    return t('tenant.tasks.errors.DATE_ORDER_INVALID_actual');
  }
  if (code !== undefined && KNOWN_TASK_ERROR_CODES.has(code)) {
    return t(`tenant.tasks.errors.${code}`);
  }
  return t(fallbackKey);
}
